import { gravarPreferencia, lerPreferenciaDe, removerPreferencia } from './preferenciaLocal';

/**
 * Preferência de tema — a fonte da verdade sobre a chave e o formato do valor.
 *
 * **O `index.html` repete esta chave literalmente**, num script síncrono que
 * aplica o tema antes da primeira pintura (sem ele a tela pisca clara antes de
 * escurecer, em toda carga com cache frio). Não dá para importar um módulo num
 * script síncrono sem custo maior, então a duplicação é deliberada e é o modo
 * de falha a vigiar: mudar `CHAVE` ou os valores aqui sem mudar lá faz o flash
 * voltar. O sintoma é visível, não silencioso.
 */

const CHAVE = 'tema';

/** O que o usuário pode escolher. A *ausência* de escolha é o terceiro estado. */
export const TEMAS = ['claro', 'escuro'] as const;
export type Tema = (typeof TEMAS)[number];

/**
 * A escolha explícita gravada, ou `null` para "seguir o sistema".
 *
 * `null` não é um erro nem um default de emergência: é o estado normal de quem
 * nunca tocou no controle, e é o que faz a aplicação acompanhar o sistema
 * operacional.
 */
export function lerTemaEscolhido(): Tema | null {
  return lerPreferenciaDe(CHAVE, TEMAS);
}

export function gravarTemaEscolhido(tema: Tema): void {
  gravarPreferencia(CHAVE, tema);
}

/** Descarta a escolha explícita — a aplicação volta a seguir o sistema. */
export function limparTemaEscolhido(): void {
  removerPreferencia(CHAVE);
}

/** O tema que o sistema operacional pede agora. */
export function temaDoSistema(): Tema {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'escuro'
    : 'claro';
}

/**
 * Escreve o tema no documento.
 *
 * `null` **remove** o atributo em vez de escrever um terceiro valor: é o que
 * devolve a decisão ao `@media (prefers-color-scheme: dark)` do `index.css`, e
 * o que dispensa manter "sistema" em sincronia entre o CSS e o JavaScript.
 */
export function aplicarTemaNoDocumento(tema: Tema | null): void {
  const raiz = document.documentElement;
  if (tema === null) delete raiz.dataset.theme;
  else raiz.dataset.theme = tema === 'escuro' ? 'dark' : 'light';
}
